import { useEffect, useState } from "react";

// Notification sounds, generated with the Web Audio API (no audio files to ship).
//   playChime()      - a short, soft two-note chime for every new notification.
//   playBellSound()  - the louder bell, kept for urgent task alerts (Admin and Staff).
// One mute switch (remembered per browser) silences both. Browsers keep audio locked until the
// person has tapped or typed on the page once; the first such gesture unlocks it.

const MUTE_KEY = "gmdSoundMuted";
const MUTE_EVENT = "gmd:sound-pref";

let audioCtx = null;
let lastSoundAt = 0;
let unlockInstalled = false;

const readMuted = () => {
    try { return window.localStorage.getItem(MUTE_KEY) === "1"; } catch (error) { return false; }
};
let muted = typeof window !== "undefined" ? readMuted() : false;

export const isSoundMuted = () => muted;

export const setSoundMuted = (value) => {
    muted = Boolean(value);
    try { window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); } catch (error) { /* storage blocked: the choice lasts until the page reloads */ }
    window.dispatchEvent(new CustomEvent(MUTE_EVENT, { detail: muted }));
};

// [muted, setMuted] for a switch; follows changes made in other tabs and other switches.
export const useSoundMuted = () => {
    const [value, setValue] = useState(muted);
    useEffect(() => {
        const onChange = () => setValue(readMuted());
        window.addEventListener(MUTE_EVENT, onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener(MUTE_EVENT, onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);
    return [value, (next) => { setSoundMuted(next); setValue(Boolean(next)); }];
};

// Milliseconds since anything last played; lets callers avoid sounding twice for one notification.
export const msSinceSound = () => Date.now() - lastSoundAt;

const getContext = () => {
    if (audioCtx && audioCtx.state !== "closed") return audioCtx;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioCtx = new AudioContextClass();
    return audioCtx;
};

const unlock = () => {
    const ctx = getContext();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
};

if (typeof window !== "undefined" && !unlockInstalled) {
    unlockInstalled = true;
    ["pointerdown", "keydown", "touchstart"].forEach((name) => window.addEventListener(name, unlock, { passive: true }));
}

const tone = (ctx, { freq, start = 0, volume, decay }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const at = ctx.currentTime + start;
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, at);
    gain.gain.setValueAtTime(volume, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + decay);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(at);
    osc.stop(at + decay + 0.05);
};

const play = (notes) => {
    if (muted) return;
    try {
        const ctx = getContext();
        if (!ctx) return;
        const run = () => { lastSoundAt = Date.now(); notes.forEach((note) => tone(ctx, note)); };
        if (ctx.state === "suspended") ctx.resume().then(run).catch(() => { /* still locked: no sound until the first tap */ });
        else run();
    } catch (error) { /* a sound must never break the app */ }
};

export const playChime = () => play([
    { freq: 988, start: 0, volume: 0.18, decay: 0.45 },
    { freq: 1319, start: 0.13, volume: 0.16, decay: 0.7 },
]);

export const playBellSound = () => play([
    { freq: 880, start: 0, volume: 0.5, decay: 1.5 },
    { freq: 2112, start: 0, volume: 0.1, decay: 0.8 },
]);

const showNotification = ({ title, body }) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") {
        new Notification(title || "Notification", { body });
        playBellSound();
    }
};

export default showNotification;
